import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PawPrint, Heart, Bone, Squiggle } from "./Doodles";
import archieHero from "@/assets/archie-hero.png";

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Float = ({ scrollY, range, className, children, spin = 0 }) => {
  const y = useTransform(scrollY, [0, 800], range);
  return (
    <motion.div
      className={`pointer-events-none absolute ${className}`}
      style={{ y }}
      initial={{ opacity: 0, scale: 0.5, rotate: spin - 12 }}
      animate={{ opacity: 1, scale: 1, rotate: spin }}
      transition={{ duration: 0.8, delay: 0.9, ease: "backOut" }}
    >
      {children}
    </motion.div>
  );
};

const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const fade = useTransform(scrollY, [0, 500], [1, 0]);
  const artY = useTransform(scrollY, [0, 900], [0, 90]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16 lg:pt-24" aria-label="Project Archie hero">
      {/* floating doodles with parallax */}
      <Float scrollY={scrollY} range={[0, -120]} className="left-[5%] top-[16%]" spin={-14}>
        <PawPrint className="h-12 w-12 md:h-16 md:w-16" color="#3B82F6" />
      </Float>
      <Float scrollY={scrollY} range={[0, -80]} className="left-[9%] bottom-[14%]" spin={24}>
        <Bone className="h-14 w-14 md:h-20 md:w-20" color="#16294B" />
      </Float>
      <Float scrollY={scrollY} range={[0, -60]} className="left-[38%] top-[11%] hidden md:block" spin={0}>
        <Squiggle className="h-8 w-14" />
      </Float>
      <Float scrollY={scrollY} range={[0, -170]} className="left-[44%] bottom-[24%] hidden lg:block" spin={-20}>
        <PawPrint className="h-9 w-9" color="#E63946" />
      </Float>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8">
        <motion.div style={{ opacity: fade }}>
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mb-6 inline-block -rotate-1 rounded-full border-2 border-[#16294B] bg-[#FBF3E3] px-5 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-[#16294B] shadow-[3px_3px_0_0_#16294B]"
          >
            A student-led community project
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display leading-[0.95] text-[#16294B]">
            <RevealLine delay={0.3} className="text-5xl sm:text-7xl lg:text-8xl">
              Inspired by <span className="text-[#E63946]">Archie</span>.
            </RevealLine>
            <RevealLine delay={0.5} className="text-5xl sm:text-7xl lg:text-8xl">
              Built for
            </RevealLine>
            <RevealLine delay={0.7} className="text-6xl sm:text-8xl lg:text-9xl">
              <span className="hl-underline">shelter dogs</span>.
            </RevealLine>
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="mt-8 max-w-xl text-base font-medium text-[#4A5568] md:text-lg"
          >
            We bring people together to build doghouses and donate them to local
            dog shelters — learning real hands-on skills and making tails wag along the way.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-join-cta"
              onClick={() => onNavigate("#join")}
              className="btn-press rounded-full border-2 border-[#16294B] bg-[#E63946] px-8 py-4 text-sm font-bold uppercase tracking-widest text-[#FBF3E3] shadow-[5px_5px_0_0_#16294B]"
            >
              Join a Build Day
            </button>
            <button
              data-testid="hero-mission-cta"
              onClick={() => onNavigate("#mission")}
              className="btn-press rounded-full border-2 border-[#16294B] bg-[#FBF3E3] px-8 py-4 text-sm font-bold uppercase tracking-widest text-[#16294B] shadow-[5px_5px_0_0_#16294B]"
            >
              Our 3-Step Mission
            </button>
          </motion.div>
        </motion.div>

        {/* Archie illustration — animated */}
        <motion.div style={{ y: artY }} className="relative mx-auto w-full max-w-sm sm:max-w-md lg:mx-0 lg:ml-auto lg:max-w-md lg:self-start lg:-mt-6 xl:max-w-lg">
          <motion.div
            initial={{ opacity: 0, y: 50, rotate: 7 }}
            animate={{ opacity: 1, y: 0, rotate: 2 }}
            transition={{ duration: 1, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div
              data-testid="hero-archie-art"
              animate={{ rotate: [1.4, 2.6, 1.4], y: [0, -10, 0] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
              className="relative overflow-hidden rounded-2xl border-2 border-[#16294B] bg-[#FBF3E3] shadow-[10px_10px_0_0_#16294B]"
            >
              <img
                src={archieHero}
                alt="Illustration of Archie the Cavapoo sitting beside a blue Project Archie doghouse with a build checklist and drill"
                className="w-full object-cover"
                fetchpriority="high"
              />
            </motion.div>

            <motion.span
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: -6 }}
              transition={{ delay: 1.5, type: "spring", stiffness: 260, damping: 14 }}
              className="absolute -top-5 -left-4 flex items-center gap-2 rounded-full border-2 border-[#16294B] bg-[#E8912D] px-4 py-2 font-display text-lg text-[#16294B] shadow-[3px_3px_0_0_#16294B]"
            >
              <PawPrint className="h-5 w-5" color="#16294B" /> Meet Archie
            </motion.span>

            <motion.span
              className="absolute -right-5 top-8"
              animate={{ y: [0, -14, 0], rotate: [0, 10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <Heart className="h-9 w-9" />
            </motion.span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
